import React, { useCallback, useContext, useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

import RatingsOverview from "./RatingsOverview";
import RatingsResponses from "./RatingsResponses";
import RatingsSettings from "./RatingsSettings";
import { firstDayOfMonth, today } from "./ratingHelpers";

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  header: {
    marginBottom: theme.spacing(2)
  },
  subtitle: {
    color: theme.palette.text.secondary
  },
  disabledNotice: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.warning.main}`
  },
  tabs: {
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  alignRight: {
    textAlign: "right"
  }
}));

const TicketRatings = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const isAdmin = String(user?.profile || "").toLowerCase() === "admin";

  const [tab, setTab] = useState("overview");
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [userId, setUserId] = useState("");
  const [whatsappId, setWhatsappId] = useState("");
  const [status, setStatus] = useState("");
  const [users, setUsers] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const fetchReport = useCallback(
    async (statusFilter = status) => {
      setLoading(true);
      try {
        const { data } = await api.get("/ticket-ratings/report", {
          params: {
            dateFrom,
            dateTo,
            ...(userId ? { userId } : {}),
            ...(whatsappId ? { whatsappId } : {}),
            ...(statusFilter ? { status: statusFilter } : {})
          }
        });
        setReport(data);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    },
    [dateFrom, dateTo, userId, whatsappId, status]
  );

  const fetchEnabled = useCallback(async () => {
    try {
      const { data } = await api.get("/ticket-ratings/settings");
      setEnabled(Boolean(data.enabled));
    } catch (err) {
      // Silencioso: o aviso de desligado só some se a consulta falhar.
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        // Silencioso: o filtro de atendente fica vazio se falhar.
      }
    })();
    fetchEnabled();
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = value => {
    setStatus(value);
    fetchReport(value);
  };

  const connections = (whatsApps || []).filter(
    w => !w.type || w.type === "baileys" || w.type === "cloudapi"
  );

  return (
    <div>
      <Container maxWidth="lg" className={classes.container}>
        <div className={classes.header}>
          <Typography variant="h5" color="primary">
            {i18n.t("ticketRatings.title")}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {i18n.t("ticketRatings.subtitle")}
          </Typography>
        </div>

        {!enabled && (
          <Paper className={classes.disabledNotice} variant="outlined">
            <Typography variant="body2">
              {i18n.t(
                isAdmin
                  ? "ticketRatings.disabledNotice.admin"
                  : "ticketRatings.disabledNotice.user"
              )}
            </Typography>
          </Paper>
        )}

        <Tabs
          value={tab}
          onChange={(e, value) => setTab(value)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabs}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="overview" label={i18n.t("ticketRatings.tabs.overview")} />
          <Tab value="responses" label={i18n.t("ticketRatings.tabs.responses")} />
          {isAdmin && <Tab value="settings" label={i18n.t("ticketRatings.tabs.settings")} />}
        </Tabs>

        {tab !== "settings" && (
          <Grid container spacing={3} style={{ marginBottom: 8 }}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label={i18n.t("ticketRatings.filters.from")}
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label={i18n.t("ticketRatings.filters.to")}
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={i18n.t("ticketRatings.filters.user")}
                value={userId}
                onChange={e => setUserId(e.target.value)}
                fullWidth
              >
                <MenuItem value="">{i18n.t("ticketRatings.filters.all")}</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label={i18n.t("ticketRatings.filters.connection")}
                value={whatsappId}
                onChange={e => setWhatsappId(e.target.value)}
                fullWidth
              >
                <MenuItem value="">{i18n.t("ticketRatings.filters.all")}</MenuItem>
                {connections.map(w => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2} className={classes.alignRight}>
              <ButtonWithSpinner
                loading={loading}
                onClick={() => fetchReport()}
                variant="contained"
                color="primary"
              >
                {i18n.t("ticketRatings.filters.submit")}
              </ButtonWithSpinner>
            </Grid>
          </Grid>
        )}

        {tab === "overview" && report && <RatingsOverview report={report} />}
        {tab === "responses" && report && (
          <RatingsResponses
            report={report}
            status={status}
            onStatusChange={handleStatusChange}
          />
        )}
        {tab === "settings" && isAdmin && <RatingsSettings onSaved={fetchEnabled} />}
      </Container>
    </div>
  );
};

export default TicketRatings;
